import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { useWritingStylesStyles } from './writing-styles.styles'
import { WARNING_TYPE_SPELLING, WARNING_TYPE_PROFANITY } from 'common/constants/constants'
import type { ContentWarning } from './writingStyleValidationTypes'

export interface HighlightedTextareaProps {
    value: string
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
    onFocus?: () => void
    disabled?: boolean
    highlights: ContentWarning[]
    ariaLabel: string
    describedById?: string
    scrollRevision?: number
}

const PADDING_V = 12

interface TextSegment {
    text: string
    startOffset: number
    warning?: ContentWarning
}

/**
 * Splits `text` into plain and highlighted segments using character offsets from
 * ContentWarning. Entries with invalid or out-of-bounds offset/length are dropped
 * as a render-time defense-in-depth guard (primary sanitization happens upstream
 * in sanitizeContentWarnings before warnings enter component state).
 */
const buildSegments = (text: string, highlights: ContentWarning[]): TextSegment[] => {
    if (!highlights.length) return [{ text, startOffset: 0 }]

    const valid = highlights.filter(
        (w) => w.offset >= 0 && w.length > 0 && w.offset + w.length <= text.length
    )

    if (!valid.length) return [{ text, startOffset: 0 }]

    const sorted = [...valid].sort((a, b) => a.offset - b.offset)
    const segments: TextSegment[] = []
    let cursor = 0

    for (const warning of sorted) {
        const start = warning.offset
        const end = Math.min(start + warning.length, text.length)
        if (start >= text.length) break
        if (start < cursor) continue
        if (start > cursor) {
            segments.push({ text: text.slice(cursor, start), startOffset: cursor })
        }
        segments.push({ text: text.slice(start, end), startOffset: start, warning })
        cursor = end
    }

    if (cursor < text.length) {
        segments.push({ text: text.slice(cursor), startOffset: cursor })
    }
    return segments
}

/**
 * Drop-in replacement for MUI's multiline outlined TextField that overlays a
 * read-only mirror div behind a transparent-background textarea. When `highlights`
 * are provided, the matching character ranges are painted with coloured backgrounds
 * that show through the transparent textarea without disrupting normal editing.
 *
 * Technique: mirror div sits absolutely behind the textarea (z-index 0). Its text
 * is colour:transparent so only <mark> backgrounds are visible. The textarea sits
 * on top (z-index 1) with background:transparent and normal text colour, so the
 * mark backgrounds shine through and the user types as usual.
 */
const HighlightedTextarea: React.FC<HighlightedTextareaProps> = ({
    value,
    onChange,
    onFocus,
    disabled = false,
    highlights,
    ariaLabel,
    describedById,
    scrollRevision
}) => {
    const classes = useWritingStylesStyles()
    const mirrorRef = useRef<HTMLDivElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const syncScroll = useCallback((e: React.UIEvent<HTMLTextAreaElement>) => {
        if (mirrorRef.current) {
            mirrorRef.current.scrollTop = e.currentTarget.scrollTop
        }
    }, [])

    // On Windows, the native scrollbar occupies layout space in the textarea (via
    // scrollbarGutter:stable) but the mirror hides its scrollbar without reserving.
    useLayoutEffect(() => {
        if (!mirrorRef.current || !textareaRef.current) return
        const syncPadding = () => {
            const taStyle = window.getComputedStyle(textareaRef.current!)
            const taPaddingRight = parseFloat(taStyle.paddingRight) || 0
            const taClientWidth = textareaRef.current!.clientWidth
            const textareaContentWidth =
                taClientWidth - parseFloat(taStyle.paddingLeft) - taPaddingRight

            const mirrorStyle = window.getComputedStyle(mirrorRef.current!)
            const mirrorPaddingLeft = parseFloat(mirrorStyle.paddingLeft) || 0
            const mirrorClientWidth = mirrorRef.current!.clientWidth
            const targetMirrorPaddingRight =
                mirrorClientWidth - mirrorPaddingLeft - textareaContentWidth
            if (targetMirrorPaddingRight > 0) {
                mirrorRef.current!.style.paddingRight = `${targetMirrorPaddingRight}px`
            }
        }
        syncPadding()
        const ro = new ResizeObserver(syncPadding)
        if (textareaRef.current) ro.observe(textareaRef.current)
        if (mirrorRef.current) ro.observe(mirrorRef.current)
        return () => ro.disconnect()
    }, [])


    useEffect(() => {
        if (!scrollRevision) return
        const raf = requestAnimationFrame(() => {
            const firstMark = mirrorRef.current?.querySelector('mark')
            if (!firstMark || !mirrorRef.current || !textareaRef.current) return
            const scrollTop = Math.max(0, firstMark.offsetTop - PADDING_V)
            mirrorRef.current.scrollTop = scrollTop
            textareaRef.current.scrollTop = scrollTop
        })
        return () => cancelAnimationFrame(raf)
    }, [scrollRevision])

    const segments = buildSegments(value, highlights)

    const wrapperClassName = disabled
        ? `${classes.htWrapper} ${classes.htWrapperDisabled}`
        : classes.htWrapper

    const textareaClassName = disabled
        ? `${classes.htSharedLayout} ${classes.htTextarea} ${classes.htTextareaDisabled}`
        : `${classes.htSharedLayout} ${classes.htTextarea}`

    return (
        <div className={wrapperClassName}>
            <div
                ref={mirrorRef}
                aria-hidden="true"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore — inert is a valid HTML attribute but not yet in React's typings
                inert=""
                className={`${classes.htSharedLayout} ${classes.htMirror}`}>
                {segments.map((seg) => {
                    if (!seg.warning) {
                        return <span key={seg.startOffset}>{seg.text}</span>
                    }
                    let markClass = classes.htMarkSecurity
                    if (seg.warning.type === WARNING_TYPE_SPELLING) markClass = classes.htMarkSpelling
                    else if (seg.warning.type === WARNING_TYPE_PROFANITY) markClass = classes.htMarkProfanity
                    return (
                        <mark key={seg.startOffset} className={markClass}>
                            {seg.text}
                        </mark>
                    )
                })}
            </div>

            <textarea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                disabled={disabled}
                spellCheck={!disabled}
                aria-label={ariaLabel}
                aria-describedby={describedById}
                onFocus={onFocus}
                onScroll={syncScroll}
                className={textareaClassName}
            />
        </div>
    )
}

export default HighlightedTextarea
